// L'éditeur structuré du CV maître : une ligne par champ, une carte par
// expérience, formation ou projet, à réordonner, supprimer ou compléter.
//
// Né dans les options de l'extension, monté ici pour que Radar et la console
// cessent d'afficher un CV qu'on ne pouvait que regarder : leur seul moyen de
// corriger un titre mal lu par l'IA était de redéposer un PDF, donc de relancer
// l'IA et d'espérer. Un seul éditeur pour les trois surfaces, habillé par
// 'profileEditorCss' (la console n'a pas Tailwind, l'extension si : le
// composant ne dépend donc d'aucun des deux).
//
// Le composant est CONTRÔLÉ et ne sait pas enregistrer : l'extension écrit dans
// son stockage local à chaque frappe, la console attend un clic sur
// « Enregistrer ». Ce choix appartient à l'app, pas à l'éditeur.

import { useState, type ReactNode } from 'react'
import { Plus, Trash2, ChevronUp, ChevronDown, X } from 'lucide-react'
import {
  type ResumeProfile,
  type WorkItem,
  type EducationItem,
  type ProjectItem,
  type LanguageItem,
  type SocialProfile,
  emptyWork,
  emptyEducation,
  emptyProject,
  emptyLanguage,
  profileCompleteness,
} from '@ai-job-fit/engine/schema'
import { safeHref } from '@ai-job-fit/engine/links'
import { FR_PROFILE_EDITOR_LABELS, type ProfileEditorLabels } from './profileEditorLabels'

export interface ProfileEditorProps {
  profile: ResumeProfile
  onChange: (p: ResumeProfile) => void
  labels?: ProfileEditorLabels
}

// --- Primitives -----------------------------------------------------------

function Field({ label, value, onChange, placeholder, full }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; full?: boolean
}) {
  return (
    <div className={full ? 'pe-full' : undefined}>
      <label className="pe-champ">
        <span className="field-label">{label}</span>
        <input
          className={`input-field${!value ? ' pe-vide' : ''}`}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    </div>
  )
}

// Champ « Fin » d'une expérience + case « Poste actuel ». Convention JSON Resume :
// poste en cours = endDate VIDE. La case ne stocke donc rien en plus, elle
// pilote juste endDate (cochée : "" ; décochée : année courante à éditer).
function EndDateField({ value, onChange, labels }: { value: string; onChange: (v: string) => void; labels: ProfileEditorLabels }) {
  const current = value.trim() === ''
  return (
    <div>
      <label className="pe-champ">
        <span className="field-label">{labels.fieldEnd}</span>
        {current ? (
          <div className="input-field pe-aujourdhui">{labels.fieldToday}</div>
        ) : (
          <input
            className="input-field"
            value={value}
            placeholder={labels.phEndYear}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </label>
      <label className="pe-case">
        <input
          type="checkbox"
          className="checkbox"
          checked={current}
          onChange={(e) => onChange(e.target.checked ? '' : String(new Date().getFullYear()))}
        />
        {labels.fieldCurrent}
      </label>
    </div>
  )
}

function Area({ label, value, onChange, placeholder, rows = 3 }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number
}) {
  return (
    <div className="pe-full">
      <label className="pe-champ">
        <span className="field-label">{label}</span>
        <textarea
          className={`input-field pe-area${!value ? ' pe-vide' : ''}`}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    </div>
  )
}

function IconButton({ onClick, title, disabled, danger, children }: {
  onClick: () => void; title: string; disabled?: boolean; danger?: boolean; children: ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`pe-icone${danger ? ' pe-danger' : ''}`}
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  )
}

// Liste de puces éditable (highlights).
function Highlights({ items, onChange, labels }: { items: string[]; onChange: (v: string[]) => void; labels: ProfileEditorLabels }) {
  return (
    <div className="pe-full">
      <span className="field-label">{labels.highlightsLabel}</span>
      <div className="pe-pile">
        {items.map((h, i) => (
          <div key={i} className="pe-ligne">
            <textarea
              className="input-field pe-area"
              rows={1}
              value={h}
              placeholder={labels.highlightsPh}
              aria-label={`${labels.highlightsLabel} ${i + 1}`}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
            />
            <IconButton danger title={labels.delete} onClick={() => onChange(items.filter((_, j) => j !== i))}>
              <X size={16} />
            </IconButton>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...items, ''])} className="pe-ajout pe-petit">
          <Plus size={12} /> {labels.highlightsAdd}
        </button>
      </div>
    </div>
  )
}

// Prévient quand une adresse saisie ne deviendra PAS un lien : le rendu écarte
// en silence ce que `safeHref` refuse, et sans ce mot on chercherait longtemps
// pourquoi le portfolio n'apparaît pas dans le PDF.
function LinkWarning({ url, labels }: { url: string | undefined; labels: ProfileEditorLabels }) {
  if (!url?.trim() || safeHref(url)) return null
  return <p className="pe-note pe-lien-refuse">{labels.linkInvalid}</p>
}

// Liens du profil (portfolio, site, LinkedIn…), affichés cliquables dans
// l'en-tête du CV. Seule l'URL se saisit : c'est elle qu'on affiche, raccourcie.
// Le `network` d'un lien importé est conservé tel quel (norme JSON Resume).
function Links({ items, onChange, labels }: { items: SocialProfile[]; onChange: (v: SocialProfile[]) => void; labels: ProfileEditorLabels }) {
  return (
    <div className="pe-full">
      <span className="field-label">{labels.fieldLinks}</span>
      <div className="pe-pile">
        {items.map((p, i) => (
          <div key={keyOf(p)}>
            <div className="pe-ligne">
              <input
                className="input-field"
                type="url"
                inputMode="url"
                value={p.url}
                placeholder={labels.phLink}
                aria-label={`${labels.fieldLinks} ${i + 1}`}
                onChange={(e) => onChange(mapUpdate(items, i, { url: e.target.value }))}
              />
              <IconButton danger title={labels.delete} onClick={() => onChange(items.filter((_, j) => j !== i))}>
                <X size={16} />
              </IconButton>
            </div>
            <LinkWarning url={p.url} labels={labels} />
          </div>
        ))}
        <button type="button" onClick={() => onChange([...items, { network: '', url: '' }])} className="pe-ajout pe-petit">
          <Plus size={12} /> {labels.addLink}
        </button>
      </div>
    </div>
  )
}

// Carte générique d'un élément de liste (expérience, formation, projet).
function ItemCard({ title, index, count, onMove, onRemove, labels, children }: {
  title: string; index: number; count: number
  onMove: (dir: -1 | 1) => void; onRemove: () => void; labels: ProfileEditorLabels; children: ReactNode
}) {
  return (
    <div className="pe-carte">
      <div className="pe-carte-tete">
        <span className="pe-carte-titre">{title} {index + 1}</span>
        <div className="pe-actions">
          <IconButton disabled={index === 0} onClick={() => onMove(-1)} title={labels.moveUp}><ChevronUp size={16} /></IconButton>
          <IconButton disabled={index === count - 1} onClick={() => onMove(1)} title={labels.moveDown}><ChevronDown size={16} /></IconButton>
          <IconButton danger onClick={onRemove} title={labels.delete}><Trash2 size={16} /></IconButton>
        </div>
      </div>
      <div className="pe-grille">{children}</div>
    </div>
  )
}

// Compétences sous forme d'étiquettes à ajouter ou retirer.
function SkillTags({ skills, onChange, labels }: { skills: string[]; onChange: (v: string[]) => void; labels: ProfileEditorLabels }) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const parts = draft.split(',').map((s) => s.trim()).filter(Boolean)
    if (parts.length) onChange([...skills, ...parts.filter((p) => !skills.includes(p))])
    setDraft('')
  }
  return (
    <div>
      <div className="pe-tags">
        {/* key = la compétence elle-même : `add` dédoublonne et filtre les
            vides, donc chaque étiquette est unique, et la suppression au
            milieu ne décale pas les nœuds voisins (contrairement à key={index}). */}
        {skills.map((s) => (
          <span key={s} className="pe-tag">
            {s}
            <button
              type="button"
              onClick={() => onChange(skills.filter((x) => x !== s))}
              className="pe-tag-x"
              title={labels.delete}
              aria-label={`${labels.delete} : ${s}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        {skills.length === 0 && <span className="pe-vide-note">{labels.skillsEmpty}</span>}
      </div>
      <label className="pe-champ">
        <span className="field-label">{labels.skillsLabel}</span>
        <div className="pe-ligne">
          <input
            className="input-field"
            value={draft}
            placeholder={labels.skillsPh}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          />
          <button type="button" onClick={add} className="btn-primary pe-nowrap"><Plus size={16} /> {labels.skillsAdd}</button>
        </div>
      </label>
    </div>
  )
}

function Section({ title, children, onAdd, addLabel }: {
  title: string; children: ReactNode; onAdd?: () => void; addLabel?: string
}) {
  return (
    <section className="pe-section">
      <div className="pe-section-tete">
        <h3>{title}</h3>
        {onAdd && (
          <button type="button" onClick={onAdd} className="pe-ajout">
            <Plus size={16} /> {addLabel}
          </button>
        )}
      </div>
      {children}
    </section>
  )
}

// Identité stable des éléments de liste, pour les `key` React, SANS polluer le
// modèle (aucun id persisté ni envoyé à l'IA). Un WeakMap associe un id à
// chaque objet. Comme une édition immuable recrée l'objet ({...w,...patch}),
// `mapUpdate` recopie l'id de l'ancien vers le nouveau : le `key` reste stable
// pendant la frappe. Les éléments survivants d'un déplacement ou d'une
// suppression gardent leur référence, donc leur id ; un élément ajouté en
// reçoit un neuf.
//
// Pourquoi c'était nécessaire : avec `key={i}`, supprimer l'expérience 2 pendant
// qu'on tape dans la 3 faisait « glisser » l'état DOM (curseur, focus) d'une
// carte à l'autre, React réutilisant les nœuds par position.
const itemIds = new WeakMap<object, string>()
let idSeq = 0
function keyOf(item: object): string {
  let id = itemIds.get(item)
  if (id === undefined) {
    id = `item-${++idSeq}`
    itemIds.set(item, id)
  }
  return id
}

function move<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir
  if (j < 0 || j >= arr.length) return arr
  const copy = [...arr]
  ;[copy[i], copy[j]] = [copy[j], copy[i]]
  return copy
}

// Applique un patch à l'élément i en préservant son identité (id porté vers le
// nouvel objet), pour que le `key` ne change pas pendant l'édition.
function mapUpdate<T extends object>(arr: T[], i: number, patch: Partial<T>): T[] {
  return arr.map((it, j) => {
    if (j !== i) return it
    const next = { ...it, ...patch }
    const id = itemIds.get(it)
    if (id !== undefined) itemIds.set(next, id)
    return next
  })
}

// --- Composant ------------------------------------------------------------

export function ProfileEditor({ profile, onChange, labels = FR_PROFILE_EDITOR_LABELS }: ProfileEditorProps) {
  const L = labels
  const completeness = profileCompleteness(profile)
  const tone = completeness.score >= 70 ? 'good' : completeness.score >= 40 ? 'mid' : 'low'

  const setBasics = (patch: Partial<ResumeProfile['basics']>) =>
    onChange({ ...profile, basics: { ...profile.basics, ...patch } })

  const updateWork = (i: number, patch: Partial<WorkItem>) =>
    onChange({ ...profile, work: mapUpdate(profile.work, i, patch) })
  const updateEdu = (i: number, patch: Partial<EducationItem>) =>
    onChange({ ...profile, education: mapUpdate(profile.education, i, patch) })
  const updateProj = (i: number, patch: Partial<ProjectItem>) =>
    onChange({ ...profile, projects: mapUpdate(profile.projects, i, patch) })
  const updateLang = (i: number, patch: Partial<LanguageItem>) =>
    onChange({ ...profile, languages: mapUpdate(profile.languages, i, patch) })

  return (
    <div className="pe">
      <div className="pe-completude">
        <div className="pe-completude-tete">
          <span>{L.completeness}</span>
          <strong className={`pe-ton-${tone}`}>{completeness.score}%</strong>
        </div>
        <div
          className="pe-jauge"
          role="progressbar"
          aria-valuenow={completeness.score}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={L.completeness}
        >
          <div style={{ width: `${completeness.score}%` }} />
        </div>
        {completeness.weakSections.length > 0 && (
          <p className="pe-note">
            {L.completenessTodo.replace('{list}', completeness.weakSections.map((k) => L.weak[k]).join(', '))}
          </p>
        )}
      </div>

      <Section title={L.sectionIdentity}>
        <div className="pe-grille">
          <Field label={L.fieldName} value={profile.basics.name} onChange={(v) => setBasics({ name: v })} placeholder={L.phName} />
          <Field label={L.fieldLabel} value={profile.basics.label} onChange={(v) => setBasics({ label: v })} placeholder={L.phLabel} />
          <Field label={L.fieldEmail} value={profile.basics.email} onChange={(v) => setBasics({ email: v })} placeholder={L.phEmail} />
          <Field label={L.fieldPhone} value={profile.basics.phone} onChange={(v) => setBasics({ phone: v })} placeholder={L.phPhone} />
          <Field label={L.fieldLocation} value={profile.basics.location} onChange={(v) => setBasics({ location: v })} placeholder={L.phLocation} full />
          <Links items={profile.basics.profiles} onChange={(v) => setBasics({ profiles: v })} labels={L} />
          <Area label={L.fieldSummary} value={profile.basics.summary} onChange={(v) => setBasics({ summary: v })} placeholder={L.phSummary} />
        </div>
      </Section>

      <Section title={L.sectionWork} addLabel={L.addWork} onAdd={() => onChange({ ...profile, work: [...profile.work, emptyWork()] })}>
        <div className="pe-pile">
          {profile.work.length === 0 && <p className="pe-vide-note">{L.emptyWork}</p>}
          {profile.work.map((w, i) => (
            <ItemCard key={keyOf(w)} title={L.itemWork} index={i} count={profile.work.length} labels={L}
              onMove={(d) => onChange({ ...profile, work: move(profile.work, i, d) })}
              onRemove={() => onChange({ ...profile, work: profile.work.filter((_, j) => j !== i) })}>
              <Field label={L.fieldPosition} value={w.position} onChange={(v) => updateWork(i, { position: v })} />
              <Field label={L.fieldCompany} value={w.name} onChange={(v) => updateWork(i, { name: v })} />
              <Field label={L.fieldStart} value={w.startDate} onChange={(v) => updateWork(i, { startDate: v })} placeholder={L.phStart} />
              <EndDateField value={w.endDate} onChange={(v) => updateWork(i, { endDate: v })} labels={L} />
              <Highlights items={w.highlights} onChange={(v) => updateWork(i, { highlights: v })} labels={L} />
            </ItemCard>
          ))}
        </div>
      </Section>

      <Section title={L.sectionSkills}>
        <SkillTags skills={profile.skills} onChange={(v) => onChange({ ...profile, skills: v })} labels={L} />
      </Section>

      <Section title={L.sectionLanguages} addLabel={L.addLanguage} onAdd={() => onChange({ ...profile, languages: [...profile.languages, emptyLanguage()] })}>
        <div className="pe-pile">
          {profile.languages.length === 0 && <p className="pe-vide-note">{L.emptyLanguages}</p>}
          {profile.languages.map((l, i) => (
            <div key={keyOf(l)} className="pe-ligne pe-bas">
              <div className="pe-flex"><Field label={L.fieldLanguage} value={l.language} onChange={(v) => updateLang(i, { language: v })} placeholder={L.phLanguage} /></div>
              <div className="pe-flex"><Field label={L.fieldFluency} value={l.fluency} onChange={(v) => updateLang(i, { fluency: v })} placeholder={L.phFluency} /></div>
              <IconButton danger title={L.delete} onClick={() => onChange({ ...profile, languages: profile.languages.filter((_, j) => j !== i) })}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          ))}
        </div>
      </Section>

      <Section title={L.sectionEducation} addLabel={L.addEducation} onAdd={() => onChange({ ...profile, education: [...profile.education, emptyEducation()] })}>
        <div className="pe-pile">
          {profile.education.length === 0 && <p className="pe-vide-note">{L.emptyEducation}</p>}
          {profile.education.map((e, i) => (
            <ItemCard key={keyOf(e)} title={L.itemEducation} index={i} count={profile.education.length} labels={L}
              onMove={(d) => onChange({ ...profile, education: move(profile.education, i, d) })}
              onRemove={() => onChange({ ...profile, education: profile.education.filter((_, j) => j !== i) })}>
              <Field label={L.fieldDegree} value={e.studyType} onChange={(v) => updateEdu(i, { studyType: v })} />
              <Field label={L.fieldArea} value={e.area} onChange={(v) => updateEdu(i, { area: v })} />
              <Field label={L.fieldInstitution} value={e.institution} onChange={(v) => updateEdu(i, { institution: v })} full />
              <Field label={L.fieldStart} value={e.startDate} onChange={(v) => updateEdu(i, { startDate: v })} placeholder={L.phEduStart} />
              <Field label={L.fieldEnd} value={e.endDate} onChange={(v) => updateEdu(i, { endDate: v })} placeholder={L.phEduEnd} />
            </ItemCard>
          ))}
        </div>
      </Section>

      <Section title={L.sectionProjects} addLabel={L.addProject} onAdd={() => onChange({ ...profile, projects: [...profile.projects, emptyProject()] })}>
        <div className="pe-pile">
          {profile.projects.map((p, i) => (
            <ItemCard key={keyOf(p)} title={L.itemProject} index={i} count={profile.projects.length} labels={L}
              onMove={(d) => onChange({ ...profile, projects: move(profile.projects, i, d) })}
              onRemove={() => onChange({ ...profile, projects: profile.projects.filter((_, j) => j !== i) })}>
              <Field label={L.fieldProjectName} value={p.name} onChange={(v) => updateProj(i, { name: v })} full />
              <div className="pe-full">
                <Field label={L.fieldProjectUrl} value={p.url ?? ''} onChange={(v) => updateProj(i, { url: v })} placeholder={L.phLink} />
                <LinkWarning url={p.url} labels={L} />
              </div>
              <Area label={L.fieldDescription} value={p.description} onChange={(v) => updateProj(i, { description: v })} rows={2} />
              <Highlights items={p.highlights} onChange={(v) => updateProj(i, { highlights: v })} labels={L} />
            </ItemCard>
          ))}
        </div>
      </Section>
    </div>
  )
}
